import * as React from 'react'
import { Check, ChevronDown, Clock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { DateTime } from 'luxon'
import {
  TIMEZONE_MAPPING,
  TIMEZONE_ALIASES,
  US_PRIORITY_TIMEZONES,
  ADDITIONAL_TIMEZONES,
  type TimezoneEnumValue,
} from '@super/shared-types'

export type { TimezoneEnumValue }

interface TimezoneInfo {
  timezoneEnumValue: TimezoneEnumValue // UPPERCASE_UNDERSCORE format for database enum
  value: string // IANA timezone identifier for Luxon
  city: string
  region: string
  offset: string
  offsetMinutes: number
  abbreviation: string
  label: string
  zoneName: string
  offsetName: string
}

function getTimezoneInfo(timezoneEnumValue: TimezoneEnumValue): TimezoneInfo {
  const timezone = TIMEZONE_MAPPING[timezoneEnumValue]

  if (!timezone) {
    // Fallback for unknown enum values
    return {
      timezoneEnumValue,
      value: 'UTC',
      city: timezoneEnumValue.replace(/_/g, ' '),
      region: '',
      offset: 'UTC',
      offsetMinutes: 0,
      abbreviation: 'UTC',
      label: timezoneEnumValue.replace(/_/g, ' '),
      zoneName: 'UTC',
      offsetName: 'Coordinated Universal Time',
    }
  }

  try {
    const now = DateTime.now().setZone(timezone)

    // Check if timezone is valid
    if (!now.isValid) {
      throw new Error(`Invalid timezone: ${timezone}`)
    }

    // Extract city and region from timezone string
    const parts = timezone.split('/')
    const region = parts[0] || ''
    const city = parts[1]?.replace(/_/g, ' ') || timezone

    // Get offset information using Luxon
    const offset = now.toFormat('ZZ') // e.g., "-05:00"
    const offsetMinutes = now.offset

    // Try to get abbreviation from Luxon, fallback to our mapping
    let abbreviation = now.toFormat('ZZZZ') // e.g., "PST", "EST", "CST"
    let offsetName = now.toFormat('ZZZZZ') // Long offset name like "Pacific Standard Time"

    // If Luxon doesn't provide good values, use our comprehensive mapping
    const aliases = TIMEZONE_ALIASES[timezoneEnumValue] || []
    const abbreviationAlias = aliases.find(
      alias => alias.length === 3 && alias.match(/^[A-Z]{3}$/i),
    )
    const fullNameAlias = aliases.find(
      alias => alias.toLowerCase().includes('time') && alias.length > 10,
    )

    // Use better values if available - prefer our mapping for better consistency
    if (abbreviationAlias) {
      abbreviation = abbreviationAlias.toUpperCase()
    }

    if (
      fullNameAlias &&
      (!offsetName || offsetName === timezone || offsetName.startsWith('+'))
    ) {
      offsetName = fullNameAlias
    }

    // Get additional timezone names from Luxon
    const { zoneName } = now // Full zone name

    // Create a more user-friendly offset display
    const gmtOffset =
      offsetMinutes === 0
        ? 'GMT'
        : offsetMinutes > 0
          ? `GMT+${Math.floor(offsetMinutes / 60)}`
          : `GMT${Math.floor(offsetMinutes / 60)}`

    return {
      timezoneEnumValue,
      value: timezone,
      city,
      region,
      offset,
      offsetMinutes,
      abbreviation,
      label: `${city} (${abbreviation || gmtOffset})`,
      zoneName,
      offsetName,
    }
  } catch (error) {
    // Fallback for invalid timezones
    const parts = timezone.split('/')
    const city = parts[1]?.replace(/_/g, ' ') || timezone

    return {
      timezoneEnumValue,
      value: timezone,
      city,
      region: parts[0] || '',
      offset: 'UTC',
      offsetMinutes: 0,
      abbreviation: 'UTC',
      label: city,
      zoneName: timezone,
      offsetName: 'Coordinated Universal Time',
    }
  }
}

interface TimezoneSelectorProps {
  value?: TimezoneEnumValue | '' | null
  onValueChange?: (value: TimezoneEnumValue) => void
  placeholder?: string
  disabled?: boolean
}

export function TimezoneSelector({
  value,
  onValueChange,
  placeholder = 'Select timezone...',
  disabled = false,
}: TimezoneSelectorProps) {
  const [open, setOpen] = React.useState(false)

  // Memoize timezone processing for performance
  const { priorityTimezones, additionalTimezones } = React.useMemo(
    () => ({
      priorityTimezones: US_PRIORITY_TIMEZONES.map(getTimezoneInfo),
      additionalTimezones: ADDITIONAL_TIMEZONES.map(getTimezoneInfo),
    }),
    [],
  )

  const selectedTimezone = React.useMemo(() => {
    if (!value) return null
    return getTimezoneInfo(value)
  }, [value])

  const handleSelect = React.useCallback(
    (timezoneEnumValue: TimezoneEnumValue) => {
      onValueChange?.(timezoneEnumValue)
      setOpen(false)
    },
    [onValueChange],
  )

  // Render individual timezone item
  const renderTimezoneItem = React.useCallback(
    (timezone: TimezoneInfo) => {
      // Create searchable value that includes all aliases
      const aliases = TIMEZONE_ALIASES[timezone.timezoneEnumValue] || []
      const searchableValue = [
        timezone.city,
        timezone.value,
        timezone.abbreviation,
        ...aliases,
      ]
        .join(' ')
        .toLowerCase()

      return (
        <CommandItem
          key={timezone.timezoneEnumValue}
          value={searchableValue}
          onSelect={() => handleSelect(timezone.timezoneEnumValue)}
        >
          <Check
            className={cn(
              'mr-2 size-4',
              value === timezone.timezoneEnumValue
                ? 'opacity-100'
                : 'opacity-0',
            )}
          />
          <div className="flex flex-col min-w-0 flex-1">
            <div className="flex items-center justify-between">
              <span className="font-medium">{timezone.city}</span>
              <div className="flex items-baseline gap-2 text-xs text-muted-foreground font-mono">
                <span>{timezone.abbreviation}</span>
              </div>
            </div>
            <span className="text-xs text-muted-foreground truncate">
              {timezone.value}
            </span>
          </div>
        </CommandItem>
      )
    },
    [value, handleSelect],
  )

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild className="max-w-lg">
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
          disabled={disabled}
        >
          <div className="flex items-center gap-2 min-w-0">
            <Clock
              className="size-4 shrink-0 opacity-50"
              data-testid="timezone-clock-icon"
            />
            <span className="truncate">
              {selectedTimezone ? (
                <span className="flex items-baseline gap-2">
                  <span className="font-medium">{selectedTimezone.city}</span>
                  <span className="text-xs text-muted-foreground">
                    {selectedTimezone.abbreviation}
                  </span>
                </span>
              ) : (
                placeholder
              )}
            </span>
          </div>
          <ChevronDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="max-w-lg p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search by city or timezone..."
            variant="popover"
          />
          <CommandList>
            <CommandEmpty>No timezone found.</CommandEmpty>

            <CommandGroup heading="United States">
              {priorityTimezones.map(renderTimezoneItem)}
            </CommandGroup>

            <CommandSeparator />

            <CommandGroup heading="Other Locations">
              {additionalTimezones.map(renderTimezoneItem)}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
